import React, { useEffect, useState } from 'react';
import { Table, Tag, Spin, message, Button, Space, Modal, Form, Input } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  fetchProjectBids, 
  createBid, 
  updateBid, 
  deleteBid 
} from '../../features/Projects/projectSlice';

const { Option } = Input; // You can replace Input with a Select later if needed.

const BidsManagementPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const { bids, bidsLoading } = useSelector((state) => state.projects);

  // Local state for modal (create/edit)
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBid, setEditingBid] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectBids(projectId)).catch(() =>
        message.error('Failed to fetch bids.')
      );
    }
  }, [dispatch, projectId]);

  // Table columns for bid summary
  const columns = [
    { title: 'Bid ID', dataIndex: 'id', key: 'id' },
    { title: 'Translator', dataIndex: ['translator', 'name'], key: 'translator' },
    { 
      title: 'Status', 
      dataIndex: 'status', 
      key: 'status',
      render: (status) => {
        const color = status === 'submitted' ? 'green' : (status === 'pending' ? 'orange' : 'red');
        return <Tag color={color}>{status}</Tag>;
      }
    },
    { title: 'Submitted At', dataIndex: 'created_at', key: 'created_at' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEditModal(record)}>Edit</Button>
          <Button type="link" danger onClick={() => handleDelete(record.id)}>Delete</Button>
          <Button type="link" onClick={() => handleUpdateStatus(record, 'accepted')}>Accept</Button>
          <Button type="link" onClick={() => handleUpdateStatus(record, 'rejected')}>Reject</Button>
        </Space>
      ),
    },
  ];

  // Expanded row render: detailed bid info including translator details and nested service table.
  const expandedRowRender = (record) => {
    return (
      <div>
        <p><strong>Translator Details:</strong></p>
        <p>ID: {record.translator.id}</p>
        <p>Name: {record.translator.name}</p>
        {/* Add more translator details if needed */}
        <p><strong>Bid Services:</strong></p>
        {record.services && record.services.length > 0 ? (
          <Table
            columns={[
              { title: 'Service Name', dataIndex: 'service_name', key: 'service_name' },
              { title: 'Total Words', dataIndex: 'total_words', key: 'total_words' },
              { title: 'Unit Cost', dataIndex: 'unit_cost', key: 'unit_cost' },
              { title: 'Total Hours', dataIndex: 'total_hours', key: 'total_hours' },
              { title: 'Per Hour', dataIndex: 'per_hour', key: 'per_hour' },
              { title: 'Total Pages', dataIndex: 'total_pages', key: 'total_pages' },
              { title: 'Per Page', dataIndex: 'per_page', key: 'per_page' },
              { title: 'Total Quote', dataIndex: 'total_quote', key: 'total_quote' },
              { title: 'Reject Reason', dataIndex: 'reject_reason', key: 'reject_reason' },
              { title: 'Target Languages', dataIndex: 'target_languages', key: 'target_languages' },
            ]}
            dataSource={record.services}
            pagination={false}
            rowKey="service_id"
            size="small"
          />
        ) : (
          <p>No service details available.</p>
        )}
      </div>
    );
  };

  // Open modal for editing a bid.
  const openEditModal = (bid) => {
    setEditingBid(bid);
    form.setFieldsValue({
      translator_id: bid.translator.id,
      services: bid.services.map(s => ({
        service_id: s.service_id,
        total_words: s.total_words,
        unit_cost: s.unit_cost,
        total_hours: s.total_hours,
        per_hour: s.per_hour,
        total_pages: s.total_pages,
        per_page: s.per_page,
        total_quote: s.total_quote,
        reject_reason: s.reject_reason,
      }))
    });
    setModalVisible(true);
  };

  // Open modal for creating a new bid.
  const openCreateModal = () => {
    setEditingBid(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Handle modal form submission.
  const handleModalOk = () => {
    form.validateFields()
      .then(values => {
        const formData = values; // Adjust if you need to transform the data
        if (editingBid) {
          dispatch(updateBid({ projectId, bidId: editingBid.id, formData }))
            .unwrap()
            .then(() => {
              message.success('Bid updated successfully');
              setModalVisible(false);
            })
            .catch(() => message.error('Failed to update bid'));
        } else {
          dispatch(createBid({ projectId, formData }))
            .unwrap()
            .then(() => {
              message.success('Bid created successfully');
              setModalVisible(false);
            })
            .catch(() => message.error('Failed to create bid'));
        }
      })
      .catch(info => {
        console.log('Validation Failed:', info);
      });
  };

  // Handle bid deletion.
  const handleDelete = (bidId) => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: 'Are you sure you want to delete this bid?',
      onOk: () => {
        dispatch(deleteBid({ projectId, bidId }))
          .unwrap()
          .then(() => message.success('Bid deleted successfully'))
          .catch(() => message.error('Failed to delete bid'));
      },
    });
  };

  // Handle bid status update using updateBid.
  const handleUpdateStatus = (bid, newStatus) => {
    Modal.confirm({
      title: 'Update Bid Status',
      content: `Are you sure you want to mark this bid as ${newStatus}?`,
      onOk: () => {
        // Use updateBid to update the status.
        const formData = { status: newStatus };
        dispatch(updateBid({ projectId, bidId: bid.id, formData }))
          .unwrap()
          .then(() => message.success('Bid status updated successfully'))
          .catch(() => message.error('Failed to update status'));
      },
    });
  };

  // Retrieve bids for the current project (or an empty array)
  const projectBids = bids[projectId] || [];

  return (
    <div style={{ padding: '20px' }}>
      <Space style={{ marginBottom: '16px' }}>
        <Button onClick={() => navigate(-1)}>Back to Projects</Button>
        <Button type="primary" onClick={openCreateModal}>Create Bid</Button>
      </Space>
      <h2>Bids for Project ID: {projectId}</h2>
      {bidsLoading ? (
        <Spin />
      ) : (
        <Table
          dataSource={projectBids}
          columns={columns}
          expandable={{ expandedRowRender }}
          rowKey="id"
        />
      )}

      <Modal
        title={editingBid ? 'Edit Bid' : 'Create Bid'}
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleModalOk}
        width={800}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="translator_id"
            label="Translator ID"
            rules={[{ required: true, message: 'Please provide a translator ID' }]}
          >
            <Input />
            {/* Optionally, replace with a Select for available translators */}
          </Form.Item>

          <Form.List name="services">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <div
                    key={field.key}
                    style={{ border: '1px solid #f0f0f0', padding: '16px', marginBottom: '16px' }}
                  >
                    <Form.Item
                      {...field}
                      name={[field.name, 'service_id']}
                      fieldKey={[field.fieldKey, 'service_id']}
                      label="Service ID"
                      rules={[{ required: true, message: 'Please provide a service ID' }]}
                    >
                      <Input />
                      {/* Replace with a Select of available services if desired */}
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'total_words']} label="Total Words">
                      <Input type="number" />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'unit_cost']} label="Unit Cost">
                      <Input type="number" step="0.01" />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'total_hours']} label="Total Hours">
                      <Input type="number" />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'per_hour']} label="Per Hour">
                      <Input type="number" step="0.01" />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'total_pages']} label="Total Pages">
                      <Input type="number" />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'per_page']} label="Per Page">
                      <Input type="number" step="0.01" />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'total_quote']} label="Total Quote">
                      <Input type="number" step="0.01" />
                    </Form.Item>
                    <Form.Item {...field} name={[field.name, 'reject_reason']} label="Reject Reason">
                      <Input />
                    </Form.Item>
                    <Button type="link" onClick={() => remove(field.name)}>
                      Remove Service
                    </Button>
                  </div>
                ))}
                <Button type="dashed" onClick={() => add()} block>
                  + Add Service
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default BidsManagementPage;
